import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { canApprove, canApproveTransfer } from '../../lib/canApprove';
import { isBranchScopedManager, isAdminManagerOrExecutive } from '../../lib/permissions';
import { submitApproveRequest, submitDenyRequest } from '../../lib/materialRequests';
import { submitReceiveDelivery, resolveReceivePrintOption, fetchPackSnapshots } from '../../lib/materialReceive';
import ReceiveDeliverySheetContent from '../../components/inventory/ReceiveDeliverySheetContent';
import SnoozeSheet, { snoozeUntil } from '../../components/SnoozeSheet';
import OnboardingCallout from '../../components/OnboardingCallout';
// #304 D10, 79.14d. Lifted verbatim out of this file so the Purchasing Queue
// can use the same bar rather than growing a second one beside it.
import Snackbar, { useSnackbar } from '../../components/Snackbar';
import DenyReasonSheet from '../../components/inventory/DenyReasonSheet';
import { itemLabel, itemUom, trackingSummary } from '../../lib/requestItems';
import { matchesOrderSearch, matchedIdentifierKey } from '../../lib/orderIdentifiers';
import OrderIdentifiers from '../../components/OrderIdentifiers';
import { IconSearch } from '@tabler/icons-react';
import PurchaseOrderDownload from '../../components/purchasing/PurchaseOrderDownload';
import ActionButton from '../../components/ActionButton';
import StoredImage from '../../components/StoredImage';
import { INVENTORY_BUCKET } from '../../lib/storedFile';
import { transferLabel, isApprovable, isEndorsable, leadTimeDays, approveTransfer, endorseTransfer, denyTransfer, plainTransferRefusal, answerTransferLines, answeredLinesSentence, endorseTransferLines, firstStageAnsweredSentence, promisedFromShelf, PROMISING_STATUSES } from '../../lib/branchTransfers';
import { approvalShelfLine, linesPutToSupplier } from '../../lib/transferLoads';
import ShelfLine from '../../components/inventory/ShelfLine';
import PerItemAnswer from '../../components/inventory/PerItemAnswer';
import { useTier, controlHeight } from '../../lib/tiers';
import { cutOff } from '../../lib/readLimit';

// ─── Constants ────────────────────────────────────────────────────────────────

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function branchName(id) {
  return BRANCHES.find(b => b.id === id)?.name || '—';
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDateFull(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysPending(ts) {
  if (!ts) return 0;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
}

function isToday(ts) {
  if (!ts) return false;
  const d = new Date(ts);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function estTotal(items) {
  if (!items?.length) return null;
  let total = 0;
  let hasAny = false;
  for (const item of items) {
    const preferred = item.inventory_items?.vendor_items?.find(vi => vi.is_preferred);
    const cost = preferred?.vendor_price;
    if (cost != null) {
      total += Number(cost) * Number(item.qty_requested || 0);
      hasAny = true;
    }
  }
  return hasAny ? total : null;
}

function fmtCurrency(val) {
  if (val == null) return '—';
  return '$' + Number(val).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function rowAccentColor(req) {
  if (req.status === 'ordered') return '#378ADD';
  const days = daysPending(req.created_at);
  if (days >= 5) return '#E24B4A';
  return '#EF9F27';
}

function requestTitle(req) {
  const first = req.material_request_items?.[0];
  if (first?.is_off_catalog && first?.off_catalog_description) return first.off_catalog_description;
  return first?.inventory_items?.name || req.request_number;
}

function itemCount(req) {
  return req.material_request_items?.length || 0;
}

function extraItemCount(req) {
  const n = itemCount(req);
  return n > 1 ? n - 1 : 0;
}

// Carrier ETAs now live per item. material_requests.tracking_eta survives as the
// estimated arrival Mark Ordered writes, so it stays the fallback when no item has
// real carrier tracking entered yet.
function displayEta(req) {
  return trackingSummary(req.material_request_items).earliestEta || req.tracking_eta || null;
}

// ─── Sub-components (all hoisted — never defined inside parent bodies) ─────────

function BranchChip({ branchId }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 12, fontWeight: 500,
      color: '#5C5C5C', background: '#F0F0F0',
      borderRadius: 6, padding: '2px 8px',
    }}>
      {branchName(branchId)}
    </span>
  );
}

function StatusAgeChip({ req }) {
  const days = daysPending(req.created_at);
  const overdue = days >= 5;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontWeight: 600,
      // #653, Session 79.22b -- the LOCKED Safety red and Purchasing orange are
      // accents, not small text: on these tints they measured 3.55 and 2.03.
      // The darkened text values measure 6.37 and 4.88 on the same tints.
      color: overdue ? '#A32D2D' : '#9A5F00',
      background: overdue ? '#FEF0F0' : '#FEF6EA',
      border: `1px solid ${overdue ? '#F5C2C2' : '#F5D9A0'}`,
      borderRadius: 6, padding: '2px 8px',
    }}>
      {days === 0 ? 'Today' : `${days} day${days !== 1 ? 's' : ''} old`}
    </span>
  );
}

// #585 -- what the page says after somebody else decided a transfer first.
// Neutral rather than red: nothing went wrong, the answer is already given.
// Its dismiss control keeps the 40px finger floor.
function RaceNotice({ text, onDismiss }) {
  if (!text) return null;
  return (
    <div role="status" style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#F4F6F8', border: '1px solid #DCE2E8',
      borderRadius: 8, padding: '4px 4px 4px 12px', margin: '8px 12px',
      fontSize: 13, color: '#3A4550', fontWeight: 500,
    }}>
      <span style={{ flex: 1 }}>{text}</span>
      <button type="button" onClick={onDismiss} aria-label="Dismiss"
        style={{ minWidth: 40, minHeight: 40, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#3A4550' }}>
        ×
      </button>
    </div>
  );
}

function UrgencyBanner({ requests }) {
  const overdue = requests.filter(r => r.status === 'pending' && daysPending(r.created_at) >= 5);
  if (!overdue.length) return null;
  const first = overdue[0];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#FEF0F0', border: '1px solid #F5C2C2',
      borderRadius: 8, padding: '8px 12px', margin: '8px 12px',
      fontSize: 13, color: '#A32D2D', fontWeight: 500,
    }}>
      <span>⊘</span>
      <span>
        {overdue.length === 1
          ? `${first.request_number} has been waiting ${daysPending(first.created_at)} days`
          : `${overdue.length} requests waiting 5+ days`}
      </span>
    </div>
  );
}

function BranchTabs({ value, onChange, branches }) {
  const all = [{ id: null, name: 'All' }, ...branches];
  return (
    <div style={{
      display: 'flex', gap: 6, overflowX: 'auto',
      padding: '8px 12px', borderBottom: '1px solid #F0F0F0',
    }}>
      {all.map(b => (
        <button
          key={b.id ?? 'all'}
          onClick={() => onChange(b.id)}
          style={{
            padding: '5px 14px', borderRadius: 20, border: '1px solid',
            borderColor: value === b.id ? '#1c1c1e' : '#E0E0E0',
            background: value === b.id ? '#1c1c1e' : 'white',
            color: value === b.id ? 'white' : '#444',
            fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {b.name}
        </button>
      ))}
    </div>
  );
}

// ─── Transfers in this inbox. #371 and #425, Session 79.6, gate decision D13 ───
//
// WHY THEY APPEAR HERE AT ALL. A manager should have ONE place where things wait
// on them. Before this, a transfer waiting on the Dallas manager was only
// visible on the Transfers screen, so "what needs me?" had two answers on two
// pages — and the one they check every morning is this one.
//
// ── THE STRUCTURAL RULE, AND IT IS THE MOST IMPORTANT LINE IN THIS FILE ──
//
// A TRANSFER NEVER ENTERS `requests`, `needsApproval`, `activeList`,
// `selectedReq` OR ANY HANDLER ON THIS PAGE.
//
// Gate D14's silent failure is that a transfer handed to canApprove() returns
// false for EVERY manager and true for admin and executive, with no error — it
// reads `request.branch_id` and a transfer has `from_branch_id`. The sending
// manager would see a card with no Approve button and nothing in the logs. And
// handleApprove() on this page writes to `material_requests`, so a transfer
// reaching it would update a PURCHASE REQUEST with the same id.
//
// So this card carries its OWN approve and deny, calling the transfer functions
// directly. The two kinds are DISPLAYED together and HELD apart. What gets
// interleaved below is already-rendered React elements, not objects — the gate
// forbids a merged array with a type tag precisely because that keeps working
// right up until somebody adds a third card type.
//
// Approve and deny are inline rather than opening the shared detail panel, and
// that is the same rule stated once more: the panel is request-shaped from top
// to bottom, and a transfer has no vendor, no price, no tracking and no receive
// path. Two buttons on the card is also fewer taps than a panel.
export function TransferInboxRow({ transfer, branchNames, isMobile, onDone, onOpenFull, approverId = null, onRaced = null, stock = {}, promised = {}, stockState = 'loading', onMessage = null }) {
  const [mode, setMode] = useState(null);          // null | 'deny'
  // #733 -- the answer's sizes follow what is PRESSING it, not the screen width:
  // a tablet held at desk width is still a finger.
  const { coarse } = useTier();
  // #698 half (a), gate 665 D25 -- answering each item, on this card.
  const [perLine, setPerLine] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [readyBy, setReadyBy] = useState(transfer.confirmed_date || '');
  // #528, Session 79.10 -- ONE CARD, TWO STAGES, TWO DIFFERENT DATES.
  //
  //   endorsing   this is MY branch's request and I am agreeing my own people
  //               may ask. The date I can touch is NEEDED BY -- what we want.
  //               Colby: "the manager approving the request should have the
  //               ability to confirm or override the date."
  //   confirming  another branch is asking ME to supply. The date I set is
  //               READY BY -- what I promise. It is a different field and it
  //               must never overwrite theirs, or nobody can ever ask again
  //               whether we hit people's dates.
  const endorsing = transfer.status === 'requested';
  // WHO IS ASKING AND WHO WOULD SUPPLY, by name. `to_branch_id` is the branch
  // that raised it and `from_branch_id` the one being asked to give something
  // up -- the two are easy to swap and swapping them makes every sentence on
  // this card exactly backwards.
  const asker = branchNames[String(transfer.to_branch_id)] || 'A branch';
  const supplier = branchNames[String(transfer.from_branch_id)] || 'the other branch';
  const [neededBy, setNeededBy] = useState(transfer.needed_by || '');
  const days = daysPending(transfer.requested_at);
  const leadDays = leadTimeDays(transfer.needed_by);
  const lines = transfer.branch_transfer_items || [];
  const isMake = transfer.fulfilment_mode === 'make';
  // #580 part (a), gate 553 D23 -- a request for the branch's OWN shelf has
  // one confirmation and no "can we supply": the shop commits a date.
  const own = String(transfer.from_branch_id) === String(transfer.to_branch_id);
  // The names, keyed as the Transfers page keys them, so the shared answer and
  // answerTransferLines() read them the same way on both screens.
  const items = Object.fromEntries(lines.map(l => [String(l.item_id), { name: l.inventory_items?.name }]));

  // ── #698 half (a), gate 665 D25 -- "I CAN'T SUPPLY ALL OF THESE" ──────────
  //
  // Colby, reviewing the gate: "managers need to be able to deny, approve
  // individual lines of a multi item request." They could -- on the Transfers
  // sheet only. From here, where they approve everything else, a San Antonio
  // manager with the frames and no Dibond could only refuse the whole request.
  //
  // Offered exactly where the sheet offers it, and nowhere else:
  //   - the SECOND stage only, under "Confirm {supplier} can supply". The first
  //     stage's own per-item answer is D26, below.
  //   - not on a request for the branch's own shelf, whose button is "Confirm —
  //     add to To make": D25 is the supplier's answer, and a refused own-shelf
  //     request is already a dead end (#627, hosted at 79.23a).
  //   - two items or more. On one item Deny already IS the per-item answer
  //     (567 D7), and a link there would be a second way to say the same thing.
  // Who may press it is canApproveTransfer(), which put this card on the page,
  // and behind it the database's own caller check (#616).
  //
  // #698 half (b), Session 79.22b.4.7.1 -- SAN ANTONIO SEES ONLY WHAT IT IS
  // ASKED (gate 665 X2; #698 finding 5, Colby's ruling A). An item Dallas's
  // manager turned down at the first stage is never listed here, never in the
  // form, never counted, and never makes a one-item card look like two.
  const asked = linesPutToSupplier(lines, transfer);
  const offersPerItem = !endorsing && !own && asked.length > 1;
  // ── #698 half (b), gate 665 D26 -- "ONLY SOME OF THESE ARE NEEDED" ────────
  // The asking branch's manager, on her own team's request: the same form in
  // its first-stage words, two items or more (X1), never on an own-shelf
  // request (which never waits at this stage anyway).
  const offersFirstStage = endorsing && !own && lines.length > 1;

  async function run(fn, messageFor = null) {
    setBusy(true); setError(null);
    const result = await fn();
    setBusy(false);
    if (result?.error) {
      // #585 -- a sentence, never the database's shorthand. When somebody else
      // decided it first, the list reloads and this row leaves it, so the
      // message goes to the PAGE rather than dying with the row.
      const plain = plainTransferRefusal(result.error.message);
      if (plain.race && onRaced) { onRaced(plain.text); return; }
      setError(plain.text);
      return;
    }
    // #698 half (a) -- after an item-by-item answer the outcome is genuinely
    // ambiguous, and the card leaves the list the moment it reloads, so the
    // page says what happened. Built from the DATABASE's counts (gate 665 B3).
    // #698 (b) -- a first-stage answer with every item needed is exactly
    // Approve, which says nothing, so its sentence is null and nothing shows.
    const message = messageFor ? messageFor(result) : null;
    if (message && onMessage) onMessage(message);
    onDone?.();
  }

  return (
    <div style={{
      borderBottom: '1px solid #F0F0F0', padding: '11px 13px',
      /* Violet-free on purpose: this is inventory work, so it takes the
         inventory rail like everything else on the page. What distinguishes it
         is the words, not a colour nobody has been taught. */
      borderLeft: '3px solid #1D9E75', background: '#FBFEFC',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <button
          onClick={onOpenFull}
          // #735, Session 79.22b.6e -- a text link that stated no height measured
          // 15px; a finger now gets the 40 floor and a mouse 24, by the pointer
          // like the row's other controls (approved as drawn, R2).
          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#0F0F0F', minHeight: controlHeight(coarse, 'min'), display: 'inline-flex', alignItems: 'center' }}
        >
          {/* D9 and D20 — the number never renders without its direction. */}
          {transferLabel(transfer, branchNames)}
        </button>
        <span style={{ fontSize: 11, color: days >= 5 ? '#A32D2D' : '#7A7A7A', whiteSpace: 'nowrap', fontWeight: days >= 5 ? 700 : 400 }}>
          {days === 0 ? 'today' : `${days}d`}
        </span>
      </div>

      <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 3 }}>
        {/* NO PRONOUNS ON THIS CARD, and that is Colby's catch at 79.10.
            It read "Your team is asking…" and "Confirm WE can supply" — and
            both are a GUESS ABOUT WHO IS READING. Admins and executives see
            every branch's cards by design, so he was looking at Houston's
            cards while signed in at Dallas and the button told him Dallas
            could supply. His words: "WE cant supply anything we dont have
            enough. its the whole reason we're requesting a transfer."
            A branch name is true for every reader. A pronoun is true for one. */}
        {own
          ? `Asked for ${supplier}'s own shelf`
          : endorsing
            ? `${asker} is asking ${supplier} ${isMake ? 'to make these' : 'for these off their shelf'}`
            : `${asker} wants these ${isMake ? 'made' : `off ${supplier}'s shelf`}`}
        {transfer.needed_by ? ` · needed by ${transfer.needed_by}` : ''}
      </div>

      {/* #665 D1 and D15 -- EVERY ITEM, AND THE GIVING SHELF BESIDE IT. The card
          said "3 items" and a manager agreed to hand over stock without seeing
          which, or what their shelf would hold afterwards. Amber below the
          reorder point, and never a block: Approve stays usable whatever this
          says, including while the figures are still being read. */}
      {!perLine && <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
        {[...asked].sort((a, b) => Number(a.id) - Number(b.id)).map(l => (
          <div key={l.id} style={{ fontSize: 13, color: '#1A1A1A', overflowWrap: 'anywhere' }}>
            <span style={{ fontWeight: 600 }}>{Number(l.qty_requested)} {l.inventory_items?.name || `Item ${l.item_id}`}</span>
            <ShelfLine shelf={approvalShelfLine({
              qty: l.qty_requested,
              giver: supplier,
              stock: stock[`${l.item_id}:${transfer.from_branch_id}`],
              isMake,
              state: stockState,
              // #721 -- what that shelf has already promised elsewhere.
              promised: promised[`${l.item_id}:${transfer.from_branch_id}`],
            })} />
          </div>
        ))}
      </div>}

      {/* THE LEAD TIME IN DAYS, HERE, WHERE THE DECISION IS MADE.
          Found by Colby's certification run: the card showed "needed by
          2026-09-30" and nothing else, while the days count lived on the
          Transfers page — a screen the approver has no reason to open, since
          Approve is right here.
          The gate's whole argument for the number is that it is what lets the
          person committing the company judge whether it is achievable BEFORE
          they press. A date on its own makes them do the arithmetic. */}
      {leadDays != null && (
        <div style={{
          fontSize: 12, marginTop: 5, fontWeight: 600,
          color: leadDays < 0 ? '#A32D2D' : (isMake && leadDays < 7) ? '#9A5F00' : '#5C5C5C',
        }}>
          {leadDays < 0
            ? `That date was ${Math.abs(leadDays)} day${Math.abs(leadDays) === 1 ? '' : 's'} ago`
            : leadDays === 0
              ? 'Needed today'
              : `${leadDays} day${leadDays === 1 ? '' : 's'} from now`}
          {isMake && leadDays >= 0 && leadDays < 7 && ' — tight for a production run'}
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 6 }}>{error}</div>
      )}

      {/* THE PROMISED DATE, ALSO HERE. The request sheet tells the asker in so
          many words that "they will confirm a date when they accept" — so if
          the only place to give one is another screen, that promise is broken
          for anybody who approves from the inbox, which is everybody.
          Optional: a manager who does not know yet must not be forced to invent
          a date, and inventing one is worse than leaving it blank. */}
      {/* #528 -- THE ASKING BRANCH'S OWN DATE, editable by the manager agreeing
          to it. Shown on BOTH modes because a shelf transfer has a date too;
          only the maker's promise is make-only. */}
      {/* #698 (b) -- set aside while the item-by-item form is open, as the
          approved drawing shows it; the date already chosen still travels
          with the answer. */}
      {mode !== 'deny' && endorsing && !perLine && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#5C5C5C' }}>
            Needed by <span style={{ fontWeight: 400 }}>change it if they have it wrong</span>
          </label>
          <input
            type="date"
            value={neededBy}
            onChange={e => setNeededBy(e.target.value)}
            style={{ fontSize: 16, padding: '6px 8px', border: '1px solid #DDD', borderRadius: 6 }}
          />
        </div>
      )}

      {mode !== 'deny' && !perLine && !endorsing && isMake && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#5C5C5C' }}>
            Ready by <span style={{ fontWeight: 400 }}>optional — they see this</span>
          </label>
          <input
            type="date"
            value={readyBy}
            onChange={e => setReadyBy(e.target.value)}
            /* 16px minimum, locked rule 12 — anything smaller makes iOS zoom
               the page the moment it is focused, and this is a phone screen. */
            style={{ fontSize: 16, padding: '6px 8px', border: '1px solid #DDD', borderRadius: 6 }}
          />
        </div>
      )}

      {perLine && endorsing ? (
        <div style={{ marginTop: 9 }}>
          {/* #698 half (b), gate 665 D26 -- the first stage, item by item, in
              the words Colby approved at 79.22b.4.7.1's open. No shelf figure
              under each item: the drawing has none, and the question here is
              whether Dallas needs it, not whether San Antonio can spare it. */}
          <PerItemAnswer
            stage="first"
            lines={lines}
            items={items}
            supplier={supplier}
            asker={asker}
            busy={busy}
            isMobile={isMobile}
            coarse={coarse}
            onSubmit={answers => run(
              () => endorseTransferLines(transfer, answers, { branchNames, items, neededBy: neededBy || null }),
              result => firstStageAnsweredSentence(result, { supplierName: supplier, items, answers }))}
            onCancel={() => setPerLine(false)}
          />
        </div>
      ) : perLine ? (
        <div style={{ marginTop: 9 }}>
          <PerItemAnswer
            lines={asked}
            items={items}
            supplier={supplier}
            asker={asker}
            busy={busy}
            isMobile={isMobile}
            coarse={coarse}
            // D25 -- each item's shelf figure beside its yes/no, so the answer
            // and the hold-back evidence are on the same line.
            extra={l => (
              <ShelfLine shelf={approvalShelfLine({
                qty: l.qty_requested,
                giver: supplier,
                stock: stock[`${l.item_id}:${transfer.from_branch_id}`],
                isMake,
                state: stockState,
                promised: promised[`${l.item_id}:${transfer.from_branch_id}`],
              })} />
            )}
            // Called exactly as the Transfers sheet calls it (gate 665 B3):
            // one transaction, and the header follows the items (567 D4).
            onSubmit={answers => run(
              () => answerTransferLines(transfer, answers, { branchNames, items }),
              result => answeredLinesSentence(result, asker))}
            onCancel={() => setPerLine(false)}
          />
        </div>
      ) : mode !== 'deny' ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 9, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* THE TWO STAGES SAY DIFFERENT THINGS ON THE BUTTON, deliberately.
              "Approve" on both would let a Dallas endorsement read as Houston
              having agreed to supply -- which is the confusion this whole
              session was convened to remove, one level down from the tabs. */}
          <ActionButton
            onClick={() => run(() => (endorsing
              ? endorseTransfer(transfer, { branchNames, neededBy: neededBy || null })
              // #583 / 553 D26 -- who pressed Confirm, so an own-shelf request
              // tells the person who asked and never the person confirming.
              : approveTransfer(transfer, { branchNames, confirmedDate: readyBy || null, approvedBy: approverId })))}
            busy={busy} busyLabel={endorsing ? 'Sending…' : 'Confirming…'}
            className="btn btn-primary btn-sm"
            style={{ minHeight: controlHeight(coarse, 'commit') }}   /* #735 */
          >
            {endorsing
              ? `Approve & send to ${supplier}`
              : own ? 'Confirm \u2014 add to To make' : `Confirm ${supplier} can supply`}
          </ActionButton>
          <button onClick={() => setMode('deny')} className="btn btn-danger btn-sm"
            style={{ minHeight: controlHeight(coarse, 'commit') }}>
            Deny
          </button>
          {/* #698 half (a) -- the sheet's own words. A text link, but a full
              finger's height (40, the floor for a control that does not
              commit) under a finger at ANY width, and 24 with a mouse -- the
              Transfers sheet's own sizes (#727). #735: it asked the screen
              width, so a tablet at desk width got 32. */}
          {offersPerItem && (
            <button onClick={() => { setPerLine(true); setError(null); }}
              style={{ background: 'none', border: 'none', padding: '0 4px', fontSize: 12.5,
                       color: '#14503B', cursor: 'pointer', textDecoration: 'underline',
                       minHeight: controlHeight(coarse, 'min') }}>
              I can't supply all of these
            </button>
          )}
          {/* #698 half (b), gate 665 D26 -- the same link's first-stage twin, in
              F2's words, and the same finger and mouse heights. */}
          {offersFirstStage && (
            <button onClick={() => { setPerLine(true); setError(null); }}
              style={{ background: 'none', border: 'none', padding: '0 4px', fontSize: 12.5,
                       color: '#14503B', cursor: 'pointer', textDecoration: 'underline',
                       minHeight: controlHeight(coarse, 'min') }}>
              Only some of these are needed
            </button>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 9 }}>
          <textarea
            value={reason} onChange={e => setReason(e.target.value)} rows={2}
            placeholder="Why are you turning this down?"
            style={{ width: '100%', fontSize: 16, padding: 8, border: '1px solid #DDD', borderRadius: 8, boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
            <ActionButton
              onClick={() => run(() => denyTransfer(transfer, reason, { branchNames }))}
              /* #503's standard: the control never accepts a press and does
                 nothing, and the reason renders beneath rather than in a
                 tooltip — a phone shows a tooltip never. */
              blockedReason={reason.trim() ? null : 'Type a reason — it is the only message the person who asked ever sees.'}
              busy={busy} busyLabel="Denying…"
              className="btn btn-danger btn-sm"
              style={{ minHeight: controlHeight(coarse, 'commit') }}
            >
              Deny this transfer
            </ActionButton>
            <button onClick={() => { setMode(null); setReason(''); }}
              className="btn btn-ghost btn-sm" style={{ minHeight: controlHeight(coarse, 'min') }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// inFlightTabRef — first-run callouts gate, Wave 1, inv_inbox_tabs_first. Passed
// in rather than created here so the callout can live in the page that knows the
// viewer's role. One ref, attached to exactly one element: #38's first failure
// mode is the same ref on two nodes, where whichever mounts later silently wins.
function TabBar({ tab, setTab, pendingCount, inFlightCount, isMobile, inFlightTabRef }) {
  const tabs = [
    // #653, Session 79.22b -- the count sits in WHITE on these, and the locked
    // Purchasing orange and Tools blue measured 2.17 and 3.59 under it - 78w's
    // own finding, one screen over. The darkened values carry white at 5.24 and
    // 6.52; the locked colours stay what they are everywhere they are accents.
    { key: 'needs_approval', label: 'Needs approval', count: pendingCount, badgeColor: '#9A5F00' },
    { key: 'in_flight', label: 'In flight', count: inFlightCount, badgeColor: '#185FA5' },
  ];
  return (
    <div style={{
      display: 'flex',
      borderBottom: isMobile ? 'none' : '1px solid #F0F0F0',
      padding: isMobile ? '4px' : '0 12px',
      gap: isMobile ? 4 : 0,
      background: isMobile ? '#2C2C2E' : 'white',
      borderRadius: isMobile ? 10 : 0,
      margin: isMobile ? '10px 12px 0' : 0,
    }}>
      {tabs.map(t => (
        <button
          key={t.key}
          ref={t.key === 'in_flight' ? inFlightTabRef : undefined}
          onClick={() => setTab(t.key)}
          style={{
            flex: isMobile ? 1 : 'none',
            padding: isMobile ? '10px 8px' : '12px 16px',
            background: isMobile ? (tab === t.key ? 'white' : 'transparent') : 'none',
            border: 'none',
            borderBottom: !isMobile ? `2px solid ${tab === t.key ? '#1c1c1e' : 'transparent'}` : 'none',
            borderRadius: isMobile ? 8 : 0,
            fontSize: 14, fontWeight: tab === t.key ? 700 : 500,
            color: isMobile
              ? (tab === t.key ? '#1c1c1e' : '#5C5C5C')
              : (tab === t.key ? '#1c1c1e' : '#5C5C5C'),
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'all 0.15s',
          }}
        >
          {t.label}
          {t.count > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 20, height: 20, borderRadius: 10, padding: '0 5px',
              background: t.badgeColor, color: 'white',
              fontSize: 11, fontWeight: 700,
            }}>
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Desktop list row ─────────────────────────────────────────────────────────

function DesktopRow({ req, selected, onClick, searchTerm = '' }) {
  const days = daysPending(req.created_at);
  const isInFlight = req.status === 'ordered';
  const accent = rowAccentColor(req);
  const eta = displayEta(req);
  const etaToday = isToday(eta);
  const title = requestTitle(req);
  const extra = extraItemCount(req);
  const track = trackingSummary(req.material_request_items);

  return (
    <div
      onClick={onClick}
      style={{
        borderLeft: `3px solid ${accent}`,
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: '1px solid #F0F0F0',
        background: selected ? '#F0F7FF' : 'white',
        padding: '12px 14px',
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#F7F7F7'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'white'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 }}>
        <div style={{ fontSize: 11, color: '#5C5C5C' }}>{req.request_number}</div>
        <div style={{
          fontSize: 12, fontWeight: 600,
          color: isInFlight ? '#185FA5' : days >= 5 ? '#A32D2D' : days >= 3 ? '#9A5F00' : '#5C5C5C',
        }}>
          {isInFlight
            ? (etaToday ? 'ETA today' : eta ? `ETA ${fmtDate(eta)}` : 'In transit')
            : days === 0 ? '< 1 day'
            : `${days} day${days !== 1 ? 's' : ''}`}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1c1c1e', marginBottom: 4 }}>
        {title}
        {extra > 0 && <span style={{ fontWeight: 400, color: '#5C5C5C' }}> +{extra} more</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <BranchChip branchId={req.branch_id} />
        {!isInFlight && track.total > 1 && (
          <span style={{
            display: 'inline-block', fontSize: 12, fontWeight: 500,
            color: '#5C5C5C', background: '#F0F0F0',
            borderRadius: 6, padding: '2px 8px',
          }}>
            {track.total} items
          </span>
        )}
        {isInFlight && track.total > 0 && (
          <span style={{
            display: 'inline-block', fontSize: 12, fontWeight: 600,
            color: track.tracked === track.total ? '#085041' : '#185FA5',
            background: track.tracked === track.total ? '#E1F5EE' : '#E6F1FB',
            border: `1px solid ${track.tracked === track.total ? '#A8DFC9' : '#B3D4F5'}`,
            borderRadius: 6, padding: '2px 8px',
          }}>
            {track.tracked} of {track.total} tracked
          </span>
        )}
        {req.material_request_items?.[0]?.is_off_catalog && (
          <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#A32D2D', background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 6, padding: '2px 8px' }}>
            Not in catalog
          </span>
        )}
      </div>
      {/* #545. The request number is this card's headline above, so the block
          carries the five it does not already show. Only while a search is
          running: this is an approver's working list, and five extra chips on
          every row every day is noise on a screen whose job is "whose turn is
          it". When somebody IS searching, the numbers are the entire point. */}
      {searchTerm && (
        <OrderIdentifiers
          req={req}
          only={['po_ref', 'vendor_order_ref', 'work_order_ref', 'invoice_ref', 'tracking_number']}
          leadKey={matchedIdentifierKey(req, searchTerm)}
          omitEmpty
          style={{ marginTop: 6 }}
        />
      )}
    </div>
  );
}

// ─── Desktop detail panel ─────────────────────────────────────────────────────

function DesktopDetailPanel({ req, profile, onApprove, onDeny, onSnooze, onMarkReceived, saving, searchTerm = '' }) {
  const [showSnooze, setShowSnooze] = useState(false);
  const snoozeRef = useRef(null);

  useEffect(() => {
    if (!showSnooze) return;
    function handler(e) {
      if (snoozeRef.current && !snoozeRef.current.contains(e.target)) setShowSnooze(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSnooze]);

  if (!req) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-3)', fontSize: 14,
      }}>
        Select a request to view details
      </div>
    );
  }

  const days = daysPending(req.created_at);
  const isInFlight = req.status === 'ordered';
  const track = trackingSummary(req.material_request_items);
  const eta = displayEta(req);
  const etaToday = isToday(eta);
  // Issue #229 - once ordered, the committed price is frozen on material_requests.
  // estTotal() recomputes from current vendor_price, which drifts after the order is
  // placed, so an in-flight request was showing an estimate under a "Total" label.
  const total = isInFlight && req.ordered_price != null
    ? Number(req.ordered_price)
    : estTotal(req.material_request_items);
  const approved = canApprove(profile, req);
  const firstReqItem = req.material_request_items?.[0];
  const vendorName = req.inventory_vendors?.name || firstReqItem?.off_catalog_vendor_name || '—';
  const requesterName = req.users?.full_name || '—';
  const title = requestTitle(req);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 0' }}>
        <div style={{ fontSize: 12, color: '#5C5C5C', marginBottom: 6 }}>
          {requesterName} · {fmtDate(req.created_at)} ·{' '}
          {isInFlight ? `Ordered` : `${days} day${days !== 1 ? 's' : ''} pending`}
        </div>
        {/* #545, D13. THIS SCREEN CARRIED THE SHARPEST VERSION OF THAT DEFECT:
            its search box has advertised PO search in its own placeholder since
            #412, it does find a record by PO — and the record it returned then
            showed NO PO NUMBER, so the one thing the reader could not do was
            confirm they had the right order. A promise the system did not keep.
            All six now, with a labelled dash for anything absent. */}
        <OrderIdentifiers req={req} leadKey={matchedIdentifierKey(req, searchTerm)} style={{ marginBottom: 10 }} />
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1c1c1e', marginBottom: 10 }}>
          {title}
          {extraItemCount(req) > 0 && (
            <span style={{ fontWeight: 400, fontSize: 16, color: '#5C5C5C' }}> +{extraItemCount(req)} more</span>
          )}
          {' — '}{branchName(req.branch_id)}
        </div>
        {/* #413, Session 78n8.3. THIS IS WHERE THE "Order placed" EMAIL LANDS.
            That email has carried a working "Open this order" deep link since
            #126 shipped at 78h — /go/requests_inbox/:id, which opens this very
            panel. What it could not do until now was show the reader the
            purchase order, because no copy of it existed anywhere.

            So the third part of #413 was never a missing link. It was a link
            arriving at a page with nothing to show. */}
        {isInFlight && (
          <div style={{ marginBottom: 12 }}>
            <PurchaseOrderDownload poDocument={req.po_document} label="Download the PO sent to the vendor" />
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {!isInFlight && <StatusAgeChip req={req} />}
          {isInFlight && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12, fontWeight: 600, color: '#185FA5',
              background: '#E6F1FB', border: '1px solid #B3D4F5',
              borderRadius: 6, padding: '2px 8px',
            }}>
              ⇄ In transit
            </span>
          )}
          <BranchChip branchId={req.branch_id} />
          {itemCount(req) > 1 && (
            <span style={{
              display: 'inline-block', fontSize: 12, fontWeight: 500,
              color: '#5C5C5C', background: '#F0F0F0',
              borderRadius: 6, padding: '2px 8px',
            }}>
              {itemCount(req)} items
            </span>
          )}
          {vendorName !== '—' && (
            <span style={{
              display: 'inline-block', fontSize: 12, fontWeight: 500,
              color: '#5C5C5C', background: '#F0F0F0',
              borderRadius: 6, padding: '2px 8px',
            }}>
              {vendorName}
            </span>
          )}
          {etaToday && (
            <span style={{
              display: 'inline-block', fontSize: 12, fontWeight: 600,
              color: '#085041', background: '#E1F5EE',
              border: '1px solid #A8DFC9', borderRadius: 6, padding: '2px 8px',
            }}>
              Arriving today
            </span>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>

        {/* Tracking block (in flight only) - read-only mirror of Purchasing's per-item tracking */}
        {isInFlight && track.total > 0 && (
          <div style={{
            background: '#E6F1FB', borderRadius: 10,
            padding: '12px 14px', marginBottom: 16,
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#0C447C',
                textTransform: 'uppercase', letterSpacing: '.04em',
              }}>
                Tracking
              </div>
              <div style={{ fontSize: 12, color: '#185FA5' }}>
                {track.tracked} of {track.total} item{track.total !== 1 ? 's' : ''} tracked
              </div>
            </div>
            {(req.material_request_items || []).map(item => (
              <div key={item.id} style={{ padding: '6px 0' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0C447C' }}>{itemLabel(item)}</div>
                {item.tracking_number ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0C447C' }}>
                      {item.tracking_carrier ? item.tracking_carrier + ' · ' : ''}{item.tracking_number}
                    </div>
                    {item.tracking_eta && (
                      <div style={{
                        fontSize: 13, marginTop: 2,
                        color: isToday(item.tracking_eta) ? '#085041' : '#185FA5',
                        fontWeight: isToday(item.tracking_eta) ? 700 : 400,
                      }}>
                        {isToday(item.tracking_eta) ? 'Today' : fmtDateFull(item.tracking_eta)}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#5F86AE', fontStyle: 'italic' }}>
                    {item.qty_requested} {itemUom(item) || 'each'} · no tracking yet
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Items */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#5C5C5C',
            textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8,
          }}>
            {isInFlight ? 'Items ordered' : 'Items requested'}
          </div>
          {(req.material_request_items || []).map(item => (
            <div key={item.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 0', borderBottom: '1px solid #F0F0F0',
            }}>
              <span style={{ fontSize: 14, color: '#1c1c1e' }}>
                {item.is_off_catalog
                  ? (item.off_catalog_description || 'Off-catalog item')
                  : (item.inventory_items?.name || `Item #${item.item_id}`)}
              </span>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
                × {item.qty_requested} {item.inventory_items?.uom || 'each'}
              </span>
            </div>
          ))}
        </div>

        {/* Meta cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <MetaCard label={isInFlight ? 'Total' : 'Est. total'} value={fmtCurrency(total)} />
          <MetaCard label="Vendor" value={vendorName} />
          {/* The WO# card used to sit here and render only when the request
              carried one, so this grid silently changed shape between two
              requests. It is in the identifier block at the top of the panel
              now, where it keeps its label whether or not there is a number —
              #304's D15. */}
          <MetaCard label="Branch" value={branchName(req.branch_id)} />
          {isInFlight && eta && (
            <MetaCard
              label={track.tracked > 1 ? 'Earliest ETA' : 'ETA'}
              value={etaToday ? 'Today' : fmtDateFull(eta)}
              greenValue={etaToday}
            />
          )}
        </div>

        {/* Notes */}
        {req.notes && (
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#5C5C5C',
              textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6,
            }}>
              Notes
            </div>
            <div style={{
              background: '#F7F7F7', borderRadius: 8, padding: '10px 12px',
              fontSize: 13, color: '#444', lineHeight: 1.5,
            }}>
              {req.notes}
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div style={{
        padding: '14px 24px',
        borderTop: '1px solid #F0F0F0',
        display: 'flex', gap: 10, position: 'relative',
      }}>
        {!isInFlight && approved && (
          <>
            <button
              onClick={() => onApprove(req)}
              disabled={!!saving}
              style={{
                flex: 2, padding: '11px 0',
                background: '#1D9E75', border: 'none',
                borderRadius: 10, fontSize: 14, fontWeight: 700,
                color: 'white', cursor: 'pointer',
              }}
            >
              {saving === 'approve' ? 'Approving…' : '✓ Approve'}
            </button>
            <button
              onClick={() => onDeny(req)}
              disabled={!!saving}
              style={{
                flex: 1, padding: '11px 0', background: 'none',
                border: '1px solid #D0D0D0', borderRadius: 10,
                fontSize: 14, fontWeight: 600, color: '#444', cursor: 'pointer',
              }}
            >
              {saving === 'deny' ? 'Denying…' : 'Deny'}
            </button>
            <div ref={snoozeRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowSnooze(s => !s)}
                disabled={!!saving}
                style={{
                  padding: '11px 14px', background: 'none',
                  border: '1px solid #D0D0D0', borderRadius: 10,
                  fontSize: 13, fontWeight: 600, color: '#444', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                🕐 Snooze
              </button>
              {showSnooze && (
                <SnoozeSheet
                  onSnooze={opt => { setShowSnooze(false); onSnooze(req, opt); }}
                  onClose={() => setShowSnooze(false)}
                  anchorRef={snoozeRef}
                />
              )}
            </div>
          </>
        )}
        {isInFlight && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
            <button
              onClick={() => onMarkReceived(req)}
              disabled={!!saving}
              style={{
                padding: '11px 20px', background: 'none',
                border: '1px solid #D0D0D0', borderRadius: 10,
                fontSize: 14, fontWeight: 600, color: '#1c1c1e', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              ⊙ {saving === 'receive' ? 'Saving…' : 'Mark received'}
            </button>
            <span style={{ fontSize: 12, color: '#5C5C5C' }}>
              Updates stock at {branchName(req.branch_id)} automatically
            </span>
          </div>
        )}
        {!isInFlight && !approved && (
          <div style={{ fontSize: 13, color: '#5C5C5C', padding: '10px 0' }}>
            You don't have permission to approve this request.
          </div>
        )}
      </div>
    </div>
  );
}

function MetaCard({ label, value, greenValue }) {
  return (
    <div style={{ background: '#F7F7F7', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: '#5C5C5C',
        textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: greenValue ? '#085041' : '#1c1c1e' }}>
        {value}
      </div>
    </div>
  );
}

// ─── Mobile card ──────────────────────────────────────────────────────────────

function MobileCard({ req, onClick }) {
  const days = daysPending(req.created_at);
  const isInFlight = req.status === 'ordered';
  const accent = rowAccentColor(req);
  const eta = displayEta(req);
  const etaToday = isToday(eta);
  const title = requestTitle(req);
  const extra = extraItemCount(req);
  const track = trackingSummary(req.material_request_items);
  const firstItem = req.material_request_items?.[0];
  const isOffCatalog = firstItem?.is_off_catalog;
  const photoUrl = firstItem?.photo_url;

  return (
    <div
      onClick={onClick}
      style={{
        borderLeft: `3px solid ${accent}`,
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: '1px solid #F0F0F0',
        background: 'white',
        padding: '12px 14px 12px 12px',
        display: 'flex', alignItems: 'center', gap: 10,
        cursor: 'pointer',
      }}
    >
      {photoUrl && (
        <StoredImage value={photoUrl} bucket={INVENTORY_BUCKET} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#5C5C5C', marginBottom: 2 }}>{req.request_number}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 4 }}>
          {title}
          {extra > 0 && <span style={{ fontWeight: 400, color: '#5C5C5C' }}> +{extra} more</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <BranchChip branchId={req.branch_id} />
          {!isInFlight && track.total > 1 && (
            <span style={{
              display: 'inline-block', fontSize: 12, fontWeight: 500,
              color: '#5C5C5C', background: '#F0F0F0',
              borderRadius: 6, padding: '2px 8px',
            }}>
              {track.total} items
            </span>
          )}
          {isInFlight && track.total > 0 && (
            <span style={{
              display: 'inline-block', fontSize: 12, fontWeight: 600,
              color: track.tracked === track.total ? '#085041' : '#185FA5',
              background: track.tracked === track.total ? '#E1F5EE' : '#E6F1FB',
              border: `1px solid ${track.tracked === track.total ? '#A8DFC9' : '#B3D4F5'}`,
              borderRadius: 6, padding: '2px 8px',
            }}>
              {track.tracked} of {track.total} tracked
            </span>
          )}
          {isOffCatalog && (
            <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#A32D2D', background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 6, padding: '2px 8px' }}>
              Not in catalog
            </span>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 70 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: isInFlight ? '#185FA5' : days >= 5 ? '#A32D2D' : days >= 3 ? '#9A5F00' : '#5C5C5C',
          marginBottom: 4,
        }}>
          {isInFlight
            ? (etaToday ? 'ETA today' : eta ? `ETA ${fmtDate(eta)}` : 'In transit')
            : days === 0 ? '< 1 day'
            : `${days} day${days !== 1 ? 's' : ''}`}
        </div>
        <span style={{ fontSize: 18, color: 'var(--ink-3)' }}>›</span>
      </div>
    </div>
  );
}

// ─── Mobile bottom sheet ──────────────────────────────────────────────────────

function MobileBottomSheet({ req, profile, onClose, onApprove, onDeny, onSnooze, onMarkReceived, saving }) {
  const [showSnoozeSheet, setShowSnoozeSheet] = useState(false);

  if (!req) return null;

  const isInFlight = req.status === 'ordered';
  const days = daysPending(req.created_at);
  // Issue #229 - frozen ordered_price once in flight; estTotal() drifts with vendor_price.
  const total = isInFlight && req.ordered_price != null
    ? Number(req.ordered_price)
    : estTotal(req.material_request_items);
  const approved = canApprove(profile, req);
  const firstReqItem2 = req.material_request_items?.[0];
  const vendorName = req.inventory_vendors?.name || firstReqItem2?.off_catalog_vendor_name || '—';
  const requesterName = req.users?.full_name || '—';
  const track = trackingSummary(req.material_request_items);
  const eta = displayEta(req);
  const etaToday = isToday(eta);
  const title = requestTitle(req);

  const sheet = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      <div
        onMouseDown={onClose}
        onTouchEnd={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }}
      />
      <div
        onMouseDown={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'white', borderRadius: '16px 16px 0 0',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0' }} />
        </div>
        {/* Request number header */}
        <div style={{ padding: '8px 16px 0', borderBottom: '1px solid #F0F0F0' }}>
          <div style={{ fontSize: 11, color: '#5C5C5C' }}>{req.request_number}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1c1c1e', paddingBottom: 10 }}>
            {title}
            {extraItemCount(req) > 0 && (
              <span style={{ fontWeight: 400, fontSize: 14, color: '#5C5C5C' }}> +{extraItemCount(req)} more</span>
            )}
          </div>
        </div>

        {/* Snooze sub-sheet */}
        {showSnoozeSheet && (
          <SnoozeSheet
            onSnooze={opt => { setShowSnoozeSheet(false); onSnooze(req, opt); }}
            onClose={() => setShowSnoozeSheet(false)}
          />
        )}

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 16px' }}>
          <div style={{ fontSize: 12, color: '#5C5C5C', marginBottom: 8 }}>
            {req.request_number} · {requesterName} · {fmtDate(req.created_at)}
          </div>
          {/* #413 — the same control as the desktop panel, and near the top
              rather than at the bottom of the sheet. Mobile is the priority
              surface, and the approver opening this from the emailed link on a
              phone is here FOR the document; putting it below the line items
              would mean scrolling past everything to reach the one thing the
              notification promised. */}
          {isInFlight && (
            <div style={{ marginBottom: 12 }}>
              <PurchaseOrderDownload poDocument={req.po_document} label="Download the PO sent to the vendor" />
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {!isInFlight && <StatusAgeChip req={req} />}
            <BranchChip branchId={req.branch_id} />
            {etaToday && (
              <span style={{
                display: 'inline-block', fontSize: 12, fontWeight: 600,
                color: '#085041', background: '#E1F5EE',
                border: '1px solid #A8DFC9', borderRadius: 6, padding: '2px 8px',
              }}>
                Arriving today
              </span>
            )}
          </div>

          {/* Tracking (in flight) */}
          {isInFlight && track.total > 0 && (
            <div style={{
              background: '#E6F1FB', borderRadius: 10,
              padding: '10px 12px', marginBottom: 12,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: '#0C447C',
                  textTransform: 'uppercase', letterSpacing: '.04em',
                }}>
                  Tracking
                </div>
                <div style={{ fontSize: 12, color: '#185FA5' }}>
                  {track.tracked} of {track.total} tracked
                </div>
              </div>
              {(req.material_request_items || []).map(item => (
                <div key={item.id} style={{ padding: '5px 0' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0C447C' }}>{itemLabel(item)}</div>
                  {item.tracking_number ? (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0C447C' }}>
                        {item.tracking_carrier ? item.tracking_carrier + ' · ' : ''}{item.tracking_number}
                      </div>
                      {item.tracking_eta && (
                        <div style={{
                          fontSize: 13, marginTop: 3,
                          color: isToday(item.tracking_eta) ? '#085041' : '#185FA5',
                          fontWeight: isToday(item.tracking_eta) ? 700 : 400,
                        }}>
                          {isToday(item.tracking_eta) ? 'Out for delivery' : fmtDateFull(item.tracking_eta)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: '#5F86AE', fontStyle: 'italic' }}>
                      {item.qty_requested} {itemUom(item) || 'each'} · no tracking yet
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Items */}
          <div style={{ marginBottom: 12 }}>
            {(req.material_request_items || []).map(item => (
              <div key={item.id} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '8px 0', borderBottom: '1px solid #F0F0F0',
                fontSize: 14, color: '#1c1c1e',
              }}>
                <span>{item.is_off_catalog
                  ? (item.off_catalog_description || 'Off-catalog item')
                  : (item.inventory_items?.name || `Item #${item.item_id}`)}</span>
                <span style={{ color: '#555', fontWeight: 500 }}>× {item.qty_requested}</span>
              </div>
            ))}
          </div>

          {/* Est total + vendor */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase', marginBottom: 2 }}>
                {isInFlight ? 'Total' : 'Est. total'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1c1c1e' }}>{fmtCurrency(total)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase', marginBottom: 2 }}>
                Vendor
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1c1c1e' }}>{vendorName}</div>
            </div>
          </div>

          {req.notes && (
            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>{req.notes}</div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #F0F0F0', display: 'flex', gap: 10 }}>
          {!isInFlight && approved && (
            <>
              <button
                onClick={() => onApprove(req)}
                disabled={!!saving}
                style={{
                  flex: 1, padding: '14px 0', background: '#1D9E75', border: 'none',
                  borderRadius: 12, fontSize: 16, fontWeight: 700, color: 'white', cursor: 'pointer',
                }}
              >
                {saving === 'approve' ? '…' : '✓ Approve'}
              </button>
              <button
                onClick={() => onDeny(req)}
                disabled={!!saving}
                style={{
                  flex: 1, padding: '14px 0', background: 'none',
                  border: '1px solid #D0D0D0', borderRadius: 12,
                  fontSize: 16, fontWeight: 600, color: '#444', cursor: 'pointer',
                }}
              >
                {saving === 'deny' ? '…' : 'Deny'}
              </button>
              <button
                onClick={() => setShowSnoozeSheet(true)}
                disabled={!!saving}
                style={{
                  padding: '14px 16px', background: 'none',
                  border: '1px solid #D0D0D0', borderRadius: 12,
                  fontSize: 18, cursor: 'pointer',
                }}
              >
                🕐
              </button>
            </>
          )}
          {isInFlight && (
            <button
              onClick={() => onMarkReceived(req)}
              disabled={!!saving}
              style={{
                flex: 1, padding: '14px 0', background: 'none',
                border: '1px solid #D0D0D0', borderRadius: 12,
                fontSize: 16, fontWeight: 600, color: '#1c1c1e', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              ⊙ {saving === 'receive' ? 'Saving…' : 'Mark received'}
            </button>
          )}
          {!isInFlight && !approved && (
            <div style={{ flex: 1, textAlign: 'center', fontSize: 13, color: '#5C5C5C', padding: '14px 0' }}>
              View only
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(sheet, document.body);
}

// ─── Receive sheet wrapper (portal) ──────────────────────────────────────────

function ReceiveSheetPortal({ req, profile, canOverride, isMobile, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [doneResult, setDoneResult] = useState(null);
  const [serverError, setServerError] = useState('');
  const [printOption, setPrintOption] = useState(null);
  const [printDescription, setPrintDescription] = useState('Checking printer...');
  // #333. Pack sizes for every catalog line on this delivery, read once for the
  // whole sheet from the vendor this order was placed with. An empty Map is the
  // correct starting state: packagesFor() treats a missing pack size as 1, which
  // is one label per unit — exactly what this sheet did before #333.
  const [packs, setPacks] = useState(() => new Map());

  useEffect(() => {
    resolveReceivePrintOption(branchName(req.branch_id)).then(({ option, description }) => {
      setPrintOption(option);
      setPrintDescription(description);
    });
  }, [req.branch_id]);

  useEffect(() => {
    let cancelled = false;
    const itemIds = (req.material_request_items || []).map(it => it.item_id).filter(Boolean);
    fetchPackSnapshots(itemIds, req.vendor_id || null).then(map => {
      if (!cancelled) setPacks(map);
    });
    return () => { cancelled = true; };
  }, [req.id, req.vendor_id, req.material_request_items]);

  // Catalog promotion deliberately has no entry point here. It lives on the
  // Off-Catalog Review page (#243), which is master-data governance with its own
  // screen, queue and owner — the pattern SAP and Coupa both use, and neither
  // offers a promote step anywhere in requisition, order or goods receipt. The
  // off-catalog row still says so, so the receiver knows the line is accounted
  // for rather than ignored. Removed Session 74q; do not re-add it to a
  // transaction screen.
  async function handleSubmit({ lines, jobRef, notes, packingSlip, printLabels }) {
    if (!profile) return;
    setSaving(true);
    setServerError('');
    try {
      const result = await submitReceiveDelivery({
        request: req,
        lines,
        branchId: req.branch_id,
        branchName: branchName(req.branch_id),
        jobRef,
        notes,
        packingSlip,
        printLabels,
        printOption,
        profile,
      });
      setDoneResult(result);
      setDone(true);
      // #497 — the success screen normally closes itself after 1.8 seconds, which is
      // fine for "delivery received" and useless for a warning somebody has to read
      // and act on. When the packing slip photo did not upload, the sheet stays put
      // until they press Done, so the one sentence telling them not to receive it
      // again cannot scroll past in under two seconds.
      if (result?.photoFailed !== true) setTimeout(onDone, 1800);
    } catch (e) {
      setServerError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const body = (
    <ReceiveDeliverySheetContent
      request={req}
      vendorName={req.inventory_vendors?.name || null}
      branchName={branchName(req.branch_id)}
      canOverride={canOverride}
      printDescription={printDescription}
      printAvailable={!!printOption}
      packs={packs}
      onSubmit={handleSubmit}
      onCancel={onClose}
      saving={saving}
      done={done}
      doneResult={doneResult}
      serverError={serverError}
    />
  );

  // Issue #204, request path only. Desktop presents the same component as a
  // centered 560px modal instead of a full-bleed bottom sheet. The three ad-hoc
  // receive paths keep their existing presentation and #204 stays open for them.
  // Horizontal padding is 20px in both presentations, which is what the sheet's
  // full-bleed over-receipt row tint is measured against.
  const sheet = isMobile ? (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200 }}>
      <div
        onMouseDown={!saving && !done ? onClose : undefined}
        onTouchEnd={!saving && !done ? onClose : undefined}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }}
      />
      <div
        onMouseDown={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'white', borderRadius: '16px 16px 0 0',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 0', flexShrink: 0 }} />
        <div style={{ padding: '20px 20px 40px', overflowY: 'auto' }}>
          {body}
        </div>
      </div>
    </div>
  ) : (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div
        onMouseDown={!saving && !done ? onClose : undefined}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }}
      />
      <div
        onMouseDown={e => e.stopPropagation()}
        style={{
          position: 'relative', background: 'white', borderRadius: 16,
          width: '100%', maxWidth: 560, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 8px 48px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ padding: '24px 20px', overflowY: 'auto' }}>
          {body}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(sheet, document.body);
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function RequestsInboxPage({ setActive, openRequestId, setOpenRequestId, openTransferRequestId, setOpenTransferRequestId }) {
  const { profile, isAdmin, isManager, isExecutive } = useAuth();
  const isBranchScoped = isBranchScopedManager({ isAdmin, isManager, isExecutive });
  const canOverrideJobRef = isAdminManagerOrExecutive({ isAdmin, isManager, isExecutive });
  // First-run callouts gate, Wave 1 — inv_inbox_tabs_first is manager-and-above.
  // Same predicate as canOverrideJobRef and deliberately its own name: two
  // things being true for the same roles today is not a reason to conflate them.
  const canSeeInboxTabsCallout = isAdminManagerOrExecutive({ isAdmin, isManager, isExecutive });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // First-run callouts gate, Wave 1 — inv_inbox_tabs_first.
  const inFlightTabRef = useRef(null);
  const [tab, setTab] = useState('needs_approval');
  const [branchFilter, setBranchFilter] = useState(null);
  const [search, setSearch] = useState('');   // #412
  const [requests, setRequests] = useState([]);
  // #371 D13, Session 79.6. ITS OWN STATE, deliberately. Nothing on this page
  // merges these two -- see TransferInboxRow's comment for why that is
  // structural rather than tidy.
  const [inboxTransfers, setInboxTransfers] = useState([]);
  // #608's amendment, gate 675 D17, Session 79.22b.5.3 -- how many transfer
  // asks arrived when the list was cut at the database's row limit, or null.
  const [transfersCutAt, setTransfersCutAt] = useState(null);
  // #665 D15 -- the supplying branch's shelf for each shelf-request item, keyed
  // `item:branch` as the Transfers page keys it.
  const [inboxStock, setInboxStock] = useState({});
  const [inboxStockState, setInboxStockState] = useState('loading');
  // #721 -- what each supplying branch has already promised off that shelf,
  // keyed the same way (promisedFromShelf()).
  const [inboxPromised, setInboxPromised] = useState({});
  // #605 -- which transfer the bell just sent us to, so its card can say so.
  const [flashTransferId, setFlashTransferId] = useState(null);
  // #585 -- set when a transfer this person pressed was decided by someone else.
  const [raceNotice, setRaceNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReq, setSelectedReq] = useState(null);
  const [saving, setSaving] = useState(null);
  const [receiveReq, setReceiveReq] = useState(null);
  // #397 — the request awaiting a denial reason. Null means the sheet is shut.
  const [denyTarget, setDenyTarget] = useState(null);
  const { snackbar, showSnackbar, dismiss: dismissSnackbar } = useSnackbar();

  // Resize listener — never static check at mount
  useEffect(() => {
    function handler() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Load requests
  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('material_requests')
      .select(`
        id, request_number, branch_id, requested_by, vendor_id, status,
        department_id,
        notes, created_at, updated_at, work_order_ref, po_ref, po_document,
        vendor_order_ref, invoice_ref,
        tracking_carrier, tracking_number, tracking_eta, ordered_price,
        material_request_items (
          id, item_id, qty_requested, qty_received, notes,
          is_off_catalog, off_catalog_description, off_catalog_uom,
          off_catalog_vendor_name, photo_url, needed_by,
          tracking_carrier, tracking_number, tracking_eta,
          inventory_items (
            id, name, sku, uom,
            vendor_items ( id, vendor_id, vendor_price, is_preferred )
          )
        ),
        inventory_vendors ( id, name ),
        users!material_requests_requested_by_fkey ( id, full_name )
      `)
      .in('status', ['pending', 'ordered'])
      .order('created_at', { ascending: true })
      .order('id', { foreignTable: 'material_request_items', ascending: true });

    if (isBranchScoped) {
      query = query.eq('branch_id', profile.branch_id);
    }

    const { data, error } = await query;
    if (!error) setRequests(data || []);

    // #371 D13. A SEPARATE query, and it is scoped to the SENDING branch
    // because the sending branch is the one giving something up (D3). Reading
    // ** THE COMMENT BELOW WAS TRUE UNTIL SESSION 79.10 AND IS NOW HALF TRUE,
    // which is why it is corrected rather than deleted. ** D3 forbade the
    // asking branch approving its own SUPPLY, and it still does. What #528 adds
    // is a stage before that, where the asking branch agrees only that its own
    // people may ask -- a different question with a different answer.
    //
    // So this query is no longer filtered by branch at all; canApproveTransfer()
    // decides which stage each manager is looking at. Superseded text follows.
    //
    // to_branch_id here would put a transfer in the queue of the branch that
    // ASKED for it, which is the one outcome D3 exists to prevent.
    //
    // Not branch-filtered on `isBranchScoped` the way the request query is:
    // from_branch_id IS the filter, and it is the right one for every role.
    // canApproveTransfer() below decides who may actually act.
    const tRead = await supabase
      .from('branch_transfers')
      // #665 D1 -- the item's NAME rides along, because the card now lists them.
      // #698 (b), 79.22b.4.7.1 -- and WHO REFUSED each item, so San Antonio's
      // card leaves off what Dallas turned down (linesPutToSupplier()). Without
      // it the rule falls back to the transfer's stage and names San Antonio.
      .select('*, branch_transfer_items(id, item_id, qty_requested, line_status, refused_by_branch_id, refusal_reason, inventory_items(name))', { count: 'exact' })   // '*' carries needed_by and confirmed_date
      // #528, Session 79.10 -- BOTH STAGES. `requested` no longer means
      // "waiting on the supplying branch": it now means "waiting on the branch
      // that ASKED", and the supply question moved to awaiting_confirmation.
      //
      // Fetching only one of them would have emptied this inbox for whichever
      // manager the remaining status did not belong to -- silently, because a
      // query returning no rows looks exactly like having nothing to approve.
      .in('status', ['requested', 'awaiting_confirmation'])
      .order('requested_at', { ascending: true });
    const { data: tData, error: tErr } = tRead;
    if (!tErr) setInboxTransfers(tData || []);
    // #608's amendment (R1/R2 at 79.22b.5.3) -- every ask still waiting for a
    // yes, company-wide, is one list; past the database's row limit the rest
    // would be missing from an approver's inbox with nothing said. Gate 675
    // D17's rule: a cut list says so. The oldest arrive first here, so what
    // is cut is the newest.
    setTransfersCutAt(!tErr && cutOff(tRead) ? (tData || []).length : null);

    // #665 D15 -- ONE read for every shelf request's items at their supplying
    // branches, not one per card. Fetched at every width: #551 was this kind of
    // lookup hidden from phones. The two `.in()` filters over-fetch a few rows
    // (an item at a branch nobody asked), which is harmless -- the card looks
    // up exactly the pair it needs.
    const shelfAsks = (tData || []).filter(t =>
      t.fulfilment_mode !== 'make' && String(t.from_branch_id) !== String(t.to_branch_id));
    const shelfItemIds = [...new Set(shelfAsks.flatMap(t => (t.branch_transfer_items || []).map(l => l.item_id)))];
    const shelfBranchIds = [...new Set(shelfAsks.map(t => t.from_branch_id))];
    if (shelfItemIds.length) {
      // #721, Session 79.22b.5.1b -- AND WHAT THOSE BRANCHES HAVE ALREADY
      // PROMISED: their approved and moving transfers for these items, read
      // beside the shelf. One hop, filtered on the line, which PostgREST
      // honours. Either read failing shows "couldn't check" rather than a
      // figure that silently leaves the promises out.
      const [{ data: sRows, error: sErr }, { data: pRows, error: pErr, count: pCount }] = await Promise.all([
        supabase
          .from('inventory_stock')
          .select('item_id, branch_id, qty_on_hand, reorder_point')
          .in('item_id', shelfItemIds)
          .in('branch_id', shelfBranchIds),
        supabase
          .from('branch_transfers')
          .select('id, status, from_branch_id, to_branch_id, branch_transfer_items!inner ( item_id, qty_ready, qty_shipped, line_status )', { count: 'exact' })
          .in('status', PROMISING_STATUSES)
          .in('from_branch_id', shelfBranchIds)
          .in('branch_transfer_items.item_id', shelfItemIds),
      ]);
      // #721's reader of #608's cut-off, Session 79.22b.5.3 -- a promises list
      // cut at the row limit leaves promises uncounted, so every "can spare"
      // figure would overstate. It is a failed check: "couldn't check" in words.
      const promisesCut = cutOff({ data: pRows, count: pCount });
      setInboxStock(Object.fromEntries((sRows || []).map(r => [`${r.item_id}:${r.branch_id}`, r])));
      setInboxPromised(pErr || promisesCut ? {} : promisedFromShelf(pRows || []));
      setInboxStockState(sErr || pErr || promisesCut ? 'failed' : 'ok');
    } else {
      setInboxStockState('ok');
    }

    setLoading(false);
  }, [isBranchScoped, profile]);

  useEffect(() => { load(); }, [load]);

  // Auto-open a request arriving via a notification deep link (#44/#79/#98's
  // openRequestId, routed here by notificationNav.js — see Engineering
  // Standards rule 14). Fires once data has loaded; selects the matching
  // request on both desktop (right panel) and mobile (bottom sheet) via the
  // same selectedReq state, switches to the correct tab, and clears any
  // branch filter so the row isn't hidden. Clears openRequestId whether or
  // not a match was found — a request outside this scope (already actioned,
  // wrong branch) should not keep re-checking on every future reload.
  useEffect(() => {
    if (!openRequestId || loading) return;
    const match = requests.find(r => r.id === openRequestId);
    if (match) {
      setTab(match.status === 'ordered' ? 'in_flight' : 'needs_approval');
      setBranchFilter(null);
      // #412, Session 78n8.3 — the search is cleared for exactly the reason the
      // branch filter is. Both are things the reader set earlier that would hide
      // the row a notification just sent them to; the panel would open on a
      // request the list behind it does not show.
      setSearch('');
      setSelectedReq(match);
    }
    if (setOpenRequestId) setOpenRequestId(null);
  }, [openRequestId, loading, requests, setOpenRequestId]);

  // #605, Session 79.21c -- THE SAME ARRIVAL, FOR A TRANSFER.
  //
  // Colby's ruling: the bell telling a manager a transfer needs approving opens
  // the transfer it names rather than a list of everything waiting on them.
  // His narrowing is why this keys on the DATABASE ROW ID: `transfer_number` is
  // NULL until the supplying branch confirms, which is exactly the state a
  // transfer is in when this notification is sent.
  //
  // A transfer has no detail panel on this page -- its card carries its own
  // approve and deny inline (D14's finding) -- so "opens it" means the card is
  // brought to the reader rather than a sheet opened over it. Same filter
  // clearing as the request path above and for the same reason: a branch chip
  // or a search the reader set earlier would hide the row the bell just sent
  // them to.
  useEffect(() => {
    if (!openTransferRequestId || loading) return;
    const match = inboxTransfers.find(t => t.id === openTransferRequestId);
    if (match) {
      setTab('needs_approval');
      setBranchFilter(null);
      setSearch('');
      setSelectedReq(null);
      setFlashTransferId(match.id);
    }
    // Cleared whether or not it matched -- a transfer somebody else already
    // actioned should not re-check on every future reload.
    if (setOpenTransferRequestId) setOpenTransferRequestId(null);
  }, [openTransferRequestId, loading, inboxTransfers, setOpenTransferRequestId]);

  // The card scrolls itself into view once it has actually rendered. Done in a
  // second effect rather than inside the one above because the tab switch above
  // is what CAUSES it to render -- scrolling in the same pass would look for an
  // element that does not exist yet.
  useEffect(() => {
    if (!flashTransferId) return;
    const el = document.getElementById(`transfer-card-${flashTransferId}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [flashTransferId, tab]);

  // #412, Session 78n8.3 — this page had 1,518 lines and not one text input
  // except a server-error field.
  //
  // WHAT WENT WRONG, in plain terms: a manager is told a request needs approving
  // and is given its number — in an email, in a notification, or by the person
  // who raised it. They opened this screen and there was nowhere to type it. The
  // only way to find the request was to read down the list. Tolerable at Dallas
  // with a handful open; not tolerable in January.
  //
  // #304 D6, Session 79.14d. THIS WAS A HAND-MAINTAINED COPY, and the original
  // comment is kept below because it was RIGHT about the hazard and still lost
  // to it. It says the four identifiers were copied from Invoicing rather than
  // invented, precisely so no screen could find something another could not.
  // They drifted anyway: this copy searched the work order and Invoicing's did
  // not, Invoicing searched the vendor's order number and this one did not, and
  // NEITHER ever searched the vendor's invoice number or the tracking number.
  // A shared list is the only version of "do not write a fourth variation" that
  // a later edit cannot quietly undo.
  //
  // TWO COLUMNS HAD TO BE ADDED TO THE SELECT ABOVE for it to fire at all —
  // `vendor_order_ref` and `invoice_ref`. A matcher reading a column nobody
  // selected matches nothing and looks exactly like an order that is not there.
  //
  // ORIGINAL COMMENT, #412: "THE FOUR IDENTIFIERS ARE COPIED FROM
  // InvoicingPage.js, NOT INVENTED. Receive stock, Invoicing and the Purchasing
  // Queue all already search, and all three independently resolved to the same
  // set. Writing a fourth variation is how one screen ends up unable to find
  // what the other three can."
  const term = search.trim().toLowerCase();
  const matchesSearch = r => matchesOrderSearch(r, search);

  // Derived lists
  const needsApproval = requests
    .filter(r => r.status === 'pending' && (branchFilter == null || r.branch_id === branchFilter) && matchesSearch(r))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const inFlight = requests
    .filter(r => r.status === 'ordered' && (branchFilter == null || r.branch_id === branchFilter) && matchesSearch(r))
    .sort((a, b) => {
      if (!a.tracking_eta) return 1;
      if (!b.tracking_eta) return -1;
      return new Date(a.tracking_eta) - new Date(b.tracking_eta);
    });

  const activeList = tab === 'needs_approval' ? needsApproval : inFlight;

  // #371 D13, Session 79.6. Its own derived list, from its own state.
  //
  // canApproveTransfer() is what decides membership, NOT a branch comparison
  // written here — so the person who sees the card is exactly the person the
  // Approve button will work for. notify.js's own rule for the request path
  // says it plainly: if the predicate and the audience disagree, somebody is
  // notified about work they cannot do, or can do work they were never told
  // about. Change both or neither.
  //
  // #528, Session 79.10 -- BOTH STAGES LAND HERE, which is the whole of Colby's
  // ruling: "a transfer request should run through the same approval process
  // that a normal material request runs through." A manager has ONE place where
  // things wait on them, and a transfer now waits on two different managers at
  // two different moments.
  //
  // canApproveTransfer() decides WHICH of them is looking, so this does not
  // restate the rule -- a second copy of a permission is a second copy that can
  // drift, and this page already carries the scar of one that did.
  //
  // THE BRANCH FILTER CHIP READS A DIFFERENT COLUMN PER STAGE, and getting that
  // wrong would be silent: at stage 1 the row belongs to the branch ASKING, and
  // at stage 2 to the branch being asked to give something up. Filtering both
  // on from_branch_id would hide every one of a manager's own team's requests
  // behind their own branch chip.
  const transfersNeedingMe = inboxTransfers
    .filter(t => (isApprovable(t) || isEndorsable(t))
      && canApproveTransfer(profile, t)
      && (branchFilter == null
          || (isEndorsable(t) ? t.to_branch_id : t.from_branch_id) === branchFilter))
    .sort((a, b) => new Date(a.requested_at) - new Date(b.requested_at));

  const branchNames = Object.fromEntries(BRANCHES.map(b => [String(b.id), b.name]));

  // THE INTERLEAVE, and it merges ELEMENTS rather than objects.
  //
  // D13 wants one queue answering "what is waiting on me", longest first —
  // sectioning it would make a manager check two places on one screen. But the
  // gate forbids a merged array with a type tag, because that keeps working
  // right up until somebody adds a third card type.
  //
  // So each list maps through its OWN renderer first and what gets sorted is
  // `{ at, node }` where node is already-bound JSX. A raw transfer cannot reach
  // a request component, or vice versa, because by this point there are no raw
  // objects left to mix up.
  function interleavedNeedsApproval(renderRequest) {
    if (tab !== 'needs_approval') return activeList.map(renderRequest).map((node, i) => ({ key: i, node }));
    const rows = [
      ...needsApproval.map(r => ({ at: new Date(r.created_at).getTime(), node: renderRequest(r) })),
      ...transfersNeedingMe.map(t => ({
        at: new Date(t.requested_at).getTime(),
        node: (
          // #605 -- the anchor the bell scrolls to, and the ring that says
          // WHICH transfer was named. Without the ring a manager with four
          // transfers waiting lands on a list that looks exactly like the list
          // they would have got anyway.
          <div
            key={`transfer-${t.id}`}
            id={`transfer-card-${t.id}`}
            style={flashTransferId === t.id
              ? { outline: '2px solid #1D9E75', outlineOffset: 2, borderRadius: 12 }
              : undefined}
          >
          <TransferInboxRow
            transfer={t}
            branchNames={branchNames}
            isMobile={isMobile}
            onDone={load}
            onRaced={text => { setRaceNotice(text); load(); }}
            onOpenFull={() => setActive('transfers')}
            approverId={profile?.id || null}
            stock={inboxStock}
            promised={inboxPromised}
            stockState={inboxStockState}
            onMessage={showSnackbar}
          />
          </div>
        ),
      })),
    ];
    return rows.sort((a, b) => a.at - b.at);
  }

  // The cross-tab hint, also Invoicing's pattern and the half that actually
  // closes #412. Somebody arrives holding a number and does NOT know which tab
  // its request is on — an approver told "DAL-REQ-00061 needs looking at" may
  // find purchasing already ordered it. Without this they get a bare "nothing
  // matches" on Needs approval while the record sits one tab away, which reads
  // as "the request is gone" rather than "you are on the wrong tab".
  const otherTabMatches = tab === 'needs_approval' ? inFlight : needsApproval;
  // #371 D13. Everything on the ACTIVE tab, requests and transfers, as one
  // number. Every emptiness guard reads this rather than activeList.length --
  // a Needs approval tab holding only transfers would otherwise render "No
  // requests pending approval" and hide them, which is the silent-omission
  // shape rather than an error.
  const visibleCount = activeList.length + (tab === 'needs_approval' ? transfersNeedingMe.length : 0);
  // #608's amendment, gate 675 D17 -- the one place the list could still be
  // cut names itself, in words, above the list it belongs to.
  const transfersCutNotice = tab === 'needs_approval' && transfersCutAt != null ? (
    <div role="status" style={{
      margin: '8px 12px', padding: '9px 12px', background: '#FFF8EC', border: '1px solid #F3D9A8',
      borderRadius: 8, fontSize: 13, color: '#8A5A08', lineHeight: 1.45,
    }}>
      Showing the first {transfersCutAt.toLocaleString('en-US')} transfer asks. More are waiting than this
      list can hold — answer some and the rest appear.
    </div>
  ) : null;
  const searchWidened = !!term && visibleCount === 0 && otherTabMatches.length > 0;

  // FOUND BY LIVE TEST, Session 78n8.3, and not visible from the code.
  //
  // With a request already open in the detail panel, typing a search that
  // excludes it left the panel showing that request while the list beside it
  // said "Nothing on In flight matches". One screen, two contradictory answers
  // about the same question — which is the exact defect shape 78n8.2's live test
  // hit twice, where a result banner and the line beneath it disagreed.
  //
  // The selection is dropped rather than the message softened: somebody typing
  // in the search box is looking for a DIFFERENT record, so continuing to show
  // the old one is not a courtesy. Guarded on `term` so clearing the box does
  // not also close a panel the reader opened deliberately.
  useEffect(() => {
    if (!term || !selectedReq) return;
    if (!activeList.some(r => r.id === selectedReq.id)) setSelectedReq(null);
  }, [term, selectedReq, activeList]);

  const usedBranches = BRANCHES.filter(b => requests.some(r => r.branch_id === b.id));

  // #412. Markup copied from InvoicingPage.js's own search box rather than
  // restyled, so the two reader-facing search surfaces look like one feature.
  // NOTE the deliberate absence of a fontSize: index.css sets every text input
  // to 16px globally, which is locked rule 12 — anything smaller makes iOS
  // Safari zoom the whole page when the field takes focus.
  const searchBox = (
    <div style={{ position: 'relative', padding: '10px 14px 0' }}>
      <IconSearch size={14} color="#9A9A9A" style={{ position: 'absolute', left: 25, top: 'calc(50% + 5px)', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
      <input
        type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search any order number..."
        style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 11px 0 32px', color: '#0F0F0F', background: 'white', boxSizing: 'border-box' }}
      />
    </div>
  );

  // The empty state has to distinguish "nothing is pending" from "nothing
  // matches what you typed". They are the same screen and opposite meanings:
  // the first is good news, the second is a dead end the reader can fix.
  const emptyState = (
    <div style={{ padding: 20, color: '#5C5C5C', fontSize: 13 }}>
      {searchWidened ? (
        <>
          Nothing on <b>{tab === 'needs_approval' ? 'Needs approval' : 'In flight'}</b> matches “{search.trim()}”,
          {' '}but <b>{otherTabMatches.length}</b> {otherTabMatches.length === 1 ? 'request' : 'requests'} on
          {' '}<b>{tab === 'needs_approval' ? 'In flight' : 'Needs approval'}</b> {otherTabMatches.length === 1 ? 'does' : 'do'}.
        </>
      ) : term ? (
        <>No request matches “{search.trim()}”.</>
      ) : (
        tab === 'needs_approval' ? 'No requests pending approval.' : 'No orders in flight.'
      )}
    </div>
  );


  // Actions
  async function handleApprove(req) {
    setSaving('approve');
    const prev = requests.map(r => ({ ...r }));
    setRequests(rs => rs.filter(r => r.id !== req.id));
    setSelectedReq(null);
    // #391, Session 78n — approverId added so `approved_by` is finally written
    // by the manager's approve, not only by the executive's threshold decision.
    const result = await submitApproveRequest({ requestId: req.id, approverName: profile?.full_name, approverId: profile?.id });
    if (!result.ok) {
      setRequests(prev);
      if (result.stale) {
        showSnackbar(`${req.request_number} was already handled by someone else.`);
      }
    } else {
      showSnackbar(`${req.request_number} approved`, async () => {
        await supabase.from('material_requests').update({ status: 'pending' }).eq('id', req.id);
        load();
      });
    }
    setSaving(null);
  }

  // #397, Session 78n4. Deny no longer acts on the tap — it opens the reason
  // sheet, and the sheet's confirm does the work. Both Deny buttons already
  // routed through this one handler, so the sheet is mounted once for the page
  // rather than once per surface (gate decision D9).
  function handleDeny(req) {
    setDenyTarget(req);
  }

  async function confirmDeny({ reasonId, reasonLabel, note }) {
    const req = denyTarget;
    if (!req) return;
    setSaving('deny');
    const prev = requests.map(r => ({ ...r }));
    setRequests(rs => rs.filter(r => r.id !== req.id));
    setSelectedReq(null);
    const result = await submitDenyRequest({ requestId: req.id, reasonId, reasonLabel, note });
    if (!result.ok) {
      setRequests(prev);
      if (result.stale) {
        showSnackbar(`${req.request_number} was already handled by someone else.`);
      }
    } else {
      showSnackbar(`${req.request_number} denied`, async () => {
        // Undo restores `pending` and deliberately does NOT clear the reason
        // columns. A reinstated request that still carries why it was denied
        // once is more useful than one pretending it never happened, and the
        // next denial overwrites them anyway.
        await supabase.from('material_requests').update({ status: 'pending' }).eq('id', req.id);
        load();
      });
    }
    setSaving(null);
    setDenyTarget(null);
  }

  async function handleSnooze(req, optionKey) {
    if (!profile?.id) return;
    const until = snoozeUntil(optionKey);
    if (!until) return;
    await supabase
      .from('notifications')
      .update({ snoozed_until: until })
      .eq('link_id', String(req.id))
      .in('event_type', ['restock_request', 'off_catalog_request'])
      .eq('user_id', profile.id);
  }

  function handleMarkReceived(req) {
    setReceiveReq(req);
    setSelectedReq(null);
  }

  const roleLabel = {
    admin: 'Admin',
    executive: 'Executive',
    manager: 'Manager',
    purchasing: 'Purchasing',
  }[profile?.role] || 'Team member';

  const scopeLabel =
    profile?.role === 'admin' || profile?.role === 'executive' || profile?.role === 'purchasing'
      ? 'All branches'
      : branchName(profile?.branch_id);

  // ── Desktop ───────────────────────────────────────────────────────────────

  if (!isMobile) {
    return (
      <div style={{ padding: 24, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          flex: 1, background: 'white', borderRadius: 14,
          border: '1px solid #E8E8E8', display: 'flex', flexDirection: 'column',
          overflow: 'hidden', minHeight: 0,
        }}>
          {/* Page header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px 20px', borderBottom: '1px solid #F0F0F0',
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1c1c1e' }}>Requests inbox</div>
            <div style={{ fontSize: 13, color: '#5C5C5C' }}>{scopeLabel} · {roleLabel}</div>
          </div>

          {/* Tabs */}
          <TabBar
            tab={tab}
            setTab={t => { setTab(t); setSelectedReq(null); }}
            /* #371 D13 -- one plain count of everything waiting on this
               person, requests and transfers together. Two numbers, or a
               split badge, would make somebody add them up to answer the
               only question the badge exists to answer. */
            pendingCount={needsApproval.length + transfersNeedingMe.length}
            inFlightCount={inFlight.length}
            isMobile={false}
            inFlightTabRef={inFlightTabRef}
          />

          {/* First-run callouts gate, Wave 1 — inv_inbox_tabs_first. Manager and
              above per the gate's role column, read from the shared predicate
              rather than an inline triple (locked rule 8). */}
          <OnboardingCallout
            calloutKey="inv_inbox_tabs_first"
            text="In flight is read-only — it is what purchasing has ordered, with their tracking."
            anchorRef={inFlightTabRef}
            isMobile={false}
            show={canSeeInboxTabsCallout}
          />

          {/* Split panel */}
          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* Left list */}
            <div style={{
              width: '40%', borderRight: '1px solid #F0F0F0',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              {usedBranches.length > 1 && (
                <BranchTabs value={branchFilter} onChange={setBranchFilter} branches={usedBranches} />
              )}
              {searchBox}
              {tab === 'needs_approval' && (
                <UrgencyBanner requests={needsApproval} />
              )}
              <RaceNotice text={raceNotice} onDismiss={() => setRaceNotice(null)} />
              {transfersCutNotice}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ padding: 20, color: '#5C5C5C', fontSize: 13 }}>Loading…</div>
                ) : visibleCount === 0 ? (
                  emptyState
                ) : (
                  interleavedNeedsApproval(req => (
                    <DesktopRow
                      key={req.id}
                      req={req}
                      selected={selectedReq?.id === req.id}
                      onClick={() => setSelectedReq(req)}
                      searchTerm={search}
                    />
                  )).map(r => r.node)
                )}
              </div>
            </div>

            {/* Right detail */}
            <DesktopDetailPanel
              searchTerm={search}
              req={selectedReq}
              profile={profile}
              saving={saving}
              onApprove={handleApprove}
              onDeny={handleDeny}
              onSnooze={handleSnooze}
              onMarkReceived={handleMarkReceived}
            />
          </div>
        </div>

        {receiveReq && (
          <ReceiveSheetPortal
            req={receiveReq}
            profile={profile}
            canOverride={canOverrideJobRef}
            isMobile={false}
            onClose={() => setReceiveReq(null)}
            onDone={() => { setReceiveReq(null); load(); }}
          />
        )}

        {denyTarget && (
          <DenyReasonSheet
            req={denyTarget}
            isMobile={false}
            saving={saving}
            onCancel={() => setDenyTarget(null)}
            onConfirm={confirmDeny}
          />
        )}

        {snackbar && (
          <Snackbar
            msg={snackbar.msg}
            undoFn={snackbar.undoFn}
            onDismiss={dismissSnackbar}
            isMobile={false}
          />
        )}
      </div>
    );
  }

  // ── Mobile ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#F2F2F2' }}>
      {/* Header */}
      <div style={{ background: '#1c1c1e', padding: '16px 16px 0' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'white', marginBottom: 4 }}>
          Requests inbox
        </div>
        <TabBar
          tab={tab}
          setTab={t => { setTab(t); setSelectedReq(null); }}
          pendingCount={needsApproval.length + transfersNeedingMe.length}
          inFlightCount={inFlight.length}
          isMobile={true}
          inFlightTabRef={inFlightTabRef}
        />

        {/* First-run callouts gate, Wave 1 — inv_inbox_tabs_first. */}
        <OnboardingCallout
          calloutKey="inv_inbox_tabs_first"
          text="In flight is read-only — it is what purchasing has ordered, with their tracking."
          anchorRef={null}
          isMobile={true}
          show={canSeeInboxTabsCallout}
        />
      </div>

      {/* Urgency banner */}
      {tab === 'needs_approval' && needsApproval.some(r => daysPending(r.created_at) >= 5) && (
        <UrgencyBanner requests={needsApproval} />
      )}
      <RaceNotice text={raceNotice} onDismiss={() => setRaceNotice(null)} />
      {transfersCutNotice}

      {/* #412 — the search sits above the card list on mobile too. Mobile is the
          priority surface and it is the one where reading down a list is worst:
          the person holding the request number is usually standing up. */}
      {searchBox}

      {/* Card list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 20, color: '#5C5C5C', fontSize: 13 }}>Loading…</div>
        ) : visibleCount === 0 ? (
          emptyState
        ) : (
          interleavedNeedsApproval(req => (
            <MobileCard key={req.id} req={req} onClick={() => setSelectedReq(req)} />
          )).map(r => r.node)
        )}
      </div>

      {/* Bottom sheet */}
      {selectedReq && !receiveReq && (
        <MobileBottomSheet
          req={selectedReq}
          profile={profile}
          saving={saving}
          onClose={() => setSelectedReq(null)}
          onApprove={handleApprove}
          onDeny={handleDeny}
          onSnooze={handleSnooze}
          onMarkReceived={handleMarkReceived}
        />
      )}

      {/* Receive sheet */}
      {receiveReq && (
        <ReceiveSheetPortal
          req={receiveReq}
          profile={profile}
          canOverride={canOverrideJobRef}
          isMobile={true}
          onClose={() => setReceiveReq(null)}
          onDone={() => { setReceiveReq(null); load(); }}
        />
      )}

      {denyTarget && (
        <DenyReasonSheet
          req={denyTarget}
          isMobile={true}
          saving={saving}
          onCancel={() => setDenyTarget(null)}
          onConfirm={confirmDeny}
        />
      )}

      {snackbar && (
        <Snackbar
          msg={snackbar.msg}
          undoFn={snackbar.undoFn}
          onDismiss={dismissSnackbar}
          isMobile={true}
        />
      )}
    </div>
  );
}